import 'react-json-view-lite/dist/index.css'

import { Modal } from 'antd'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { useEffect, useMemo, useState } from 'react'
import { allExpanded, darkStyles, JsonView } from 'react-json-view-lite'
import styled from 'styled-components'

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: calc(
    100vh - 12rem
  ); // Account for modal header, padding, and bottom position
  overflow-y: auto;
  overflow-x: hidden;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.label`
  font-weight: 600;
  font-size: 0.875rem;
`

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 0.875rem;
  min-height: 150px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`

const HelperText = styled.div`
  font-size: 0.75rem;
  color: #8c8c8c;
  margin-top: 0.25rem;
`

const MessagesContainer = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #1e1e1e;
  overflow: hidden;
`

const FilterInputWrapper = styled.div`
  padding: 0.5rem;
  background: #252525;
  border-bottom: 1px solid #3a3a3a;
`

const FilterInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  background: #1e1e1e;
  border: 1px solid #3a3a3a;
  border-radius: 3px;
  color: white;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;

  &::placeholder {
    color: #666;
  }

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`

const LogContainer = styled.div`
  height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
`

const LogEntry = styled.div`
  margin-bottom: 1rem;
  padding: 0.5rem;
  border: 1px solid #3a3a3a;
  border-radius: 4px;
  background: #252525;

  &:last-child {
    margin-bottom: 0;
  }
`

const EmptyMessage = styled.div`
  color: #999;
  text-align: center;
  padding: 2rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`

const ClearButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  color: #000;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }

  &:active {
    transform: scale(0.98);
  }
`

const SendButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  background: #1890ff;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: #40a9ff;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: #d9d9d9;
    cursor: not-allowed;
  }
`

interface DebugCommandModalProps {
  visible: boolean
  onClose: () => void
}

export const DebugCommandModal: React.FC<DebugCommandModalProps> = ({
  visible,
  onClose,
}) => {
  const { sendCommand, recentMessages } = useKestrelViewerContext()
  const [command, setCommand] = useState('')
  const [jsonInput, setJsonInput] = useState('')
  const [jsonError, setJsonError] = useState('')
  const [filterText, setFilterText] = useState('')
  const modalRef = React.useRef<HTMLDivElement>(null)

  const filteredMessages = useMemo(() => {
    if (!filterText.trim()) {
      return recentMessages
    }
    const lowerFilter = filterText.toLowerCase()
    return recentMessages.filter((msg) =>
      JSON.stringify(msg).toLowerCase().includes(lowerFilter)
    )
  }, [recentMessages, filterText])

  // Prevent keyboard events from reaching pixelstreaming when typing
  useEffect(() => {
    if (!visible || !modalRef.current) return

    const shouldBlockEvent = (e: KeyboardEvent): boolean => {
      const activeElement = document.activeElement
      const isInputFocused =
        activeElement &&
        (activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement) &&
        modalRef.current?.contains(activeElement)

      return (
        isInputFocused ||
        (modalRef.current?.contains(e.target as Node) ?? false)
      )
    }

    const handleKeyboardEvent = (e: KeyboardEvent) => {
      if (shouldBlockEvent(e)) {
        e.stopPropagation()
        e.stopImmediatePropagation()
      }
    }

    const options = { capture: true, passive: false } as const
    window.addEventListener('keydown', handleKeyboardEvent, options)
    window.addEventListener('keypress', handleKeyboardEvent, options)
    window.addEventListener('keyup', handleKeyboardEvent, options)

    return () => {
      window.removeEventListener('keydown', handleKeyboardEvent, options)
      window.removeEventListener('keypress', handleKeyboardEvent, options)
      window.removeEventListener('keyup', handleKeyboardEvent, options)
    }
  }, [visible])

  const validateAndSend = () => {
    setJsonError('')

    if (!command.trim()) {
      setJsonError('Command is required')
      return
    }

    let parsedJson: Record<string, unknown>
    try {
      parsedJson = JSON.parse(jsonInput)
    } catch {
      setJsonError('Invalid JSON format')
      return
    }

    if (typeof parsedJson !== 'object' || Array.isArray(parsedJson)) {
      setJsonError('JSON must be an object')
      return
    }

    const messageToSend = {
      command: command.trim(),
      ...parsedJson,
    }

    try {
      sendCommand(messageToSend)
      setJsonError('')
    } catch (error) {
      console.error('Failed to send command:', error)
      setJsonError('Failed to send command')
    }
  }

  const handleCancel = () => {
    onClose()
  }

  const handleClear = () => {
    setCommand('')
    setJsonInput('')
    setJsonError('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only stop propagation, ensure regular typing works
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
  }

  return (
    <Modal
      title='Debug Commands'
      open={visible}
      onCancel={handleCancel}
      width={700}
      mask={false}
      footer={null}
      style={{
        position: 'fixed',
        bottom: '5rem',
        right: '1rem',
        top: 'auto',
        margin: 0,
        paddingBottom: 0,
        maxHeight: 'calc(100vh - 6rem)', // Ensure modal doesn't exceed viewport
      }}
      bodyStyle={{
        maxHeight: 'calc(100vh - 12rem)', // Make body scrollable
        overflowY: 'auto',
        padding: '1.5rem',
      }}>
      <ModalContent ref={modalRef}>
        <InputGroup>
          <Label>Command</Label>
          <Input
            placeholder='e.g., zoomToStartingView'
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <HelperText>
            The command name (will be merged with JSON below)
          </HelperText>
        </InputGroup>

        <InputGroup>
          <Label>JSON Payload</Label>
          <TextArea
            placeholder={`{\n  "cameraViewId": "abc",\n  "cameraPosition": {\n    "x": 1.0,\n    "y": 1.5,\n    "z": 0.8\n  }\n}`}
            value={jsonInput}
            onChange={(e) => {
              setJsonInput(e.target.value)
              setJsonError('')
            }}
            onKeyDown={handleKeyDown}
          />
          <HelperText>
            Additional data to send with the command (must be valid JSON object)
          </HelperText>
          {jsonError && <ErrorMessage>{jsonError}</ErrorMessage>}
          <ButtonGroup>
            <ClearButton onClick={handleClear}>Clear</ClearButton>
            <SendButton onClick={validateAndSend}>Send</SendButton>
          </ButtonGroup>
        </InputGroup>

        <InputGroup>
          <Label>Received Messages (Last 100)</Label>
          <MessagesContainer>
            <FilterInputWrapper>
              <FilterInput
                placeholder='Filter messages...'
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </FilterInputWrapper>
            <LogContainer>
              {filteredMessages.length === 0 ? (
                <EmptyMessage>
                  {filterText
                    ? 'No messages match your filter'
                    : 'No messages received yet...'}
                </EmptyMessage>
              ) : (
                filteredMessages.map((msg, i) => (
                  <LogEntry key={i}>
                    <JsonView
                      data={msg}
                      shouldExpandNode={allExpanded}
                      style={darkStyles}
                    />
                  </LogEntry>
                ))
              )}
            </LogContainer>
          </MessagesContainer>
        </InputGroup>
      </ModalContent>
    </Modal>
  )
}
